import { useEffect, useRef, useState } from 'react';

import { ClickOutsideWrapper } from '@/components/layouts/ClickOutsideWrapper';

type DigitalSignageTimePickerInputProps = {
  value: string;
  onChange: (val: string) => void;
};

export const DigitalSignageTimePicker = ({
  value,
  onChange
}: DigitalSignageTimePickerInputProps) => {
  const [display, setDisplay] = useState<string>('');
  const [open, setOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  const formatTo12h = (val: string) => {
    const [hStr, mStr] = val.split(':');
    let h = Number(hStr);
    const m = Number(mStr);
    let period = 'AM';
    if (h >= 12) {
      period = 'PM';
      if (h > 12) {
        h -= 12;
      }
    }
    if (h === 0) {
      h = 12;
    }
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')} ${period}`;
  };

  const times: string[] = [];
  // First, 12:00 AM to 11:55 AM
  for (let h = 0; h < 12; h++) {
    for (let m = 0; m < 60; m += 5) {
      const hour = h === 0 ? 12 : h;
      times.push(`${hour.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')} AM`);
    }
  }
  // Then, 12:00 PM to 11:55 PM
  for (let h = 0; h < 12; h++) {
    for (let m = 0; m < 60; m += 5) {
      const hour = h === 0 ? 12 : h;
      times.push(`${hour.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')} PM`);
    }
  }

  useEffect(() => {
    if (value) {
      setDisplay(formatTo12h(value));
    }
  }, [value]);

  useEffect(() => {
    if (open && listRef.current) {
      const selectedEl = listRef.current.querySelector('[data-selected="true"]');
      if (selectedEl) {
        (selectedEl as HTMLElement).scrollIntoView({ block: 'center' });
      }
    }
  }, [open]);

  const handleSelect = (val: string) => {
    setDisplay(val);
    setOpen(false);
    const [time, period] = val.split(' ');
    if (!time || !period) {
      // fallback: do not update if format is wrong
      return;
    }
    const [hStr, mStr] = time.split(':');
    let h = Number(hStr);
    let m = Number(mStr);
    if (isNaN(h) || isNaN(m)) {
      return;
    }
    if (period === 'PM' && h !== 12) {
      h += 12;
    }
    if (period === 'AM' && h === 12) {
      h = 0;
    }
    h = Math.max(0, Math.min(23, h));
    m = Math.max(0, Math.min(59, m));
    onChange(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
  };

  return (
    <ClickOutsideWrapper className='relative' onClickOutside={() => setOpen(false)}>
      <input
        type='text'
        value={display}
        readOnly
        className='w-full cursor-pointer rounded-md border border-secondary-blue-gray px-2 py-1'
        onClick={() => setOpen((prev) => !prev)}
      />
      {open && (
        <div
          ref={listRef}
          className='no-scrollbar absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-md border bg-white shadow-lg'>
          {times.map((t) => (
            <div
              key={t}
              data-selected={t === display ? 'true' : 'false'}
              className={`cursor-pointer px-2 py-1 hover:bg-secondary-pale-blue-gray ${
                t === display ? 'bg-secondary-blue-gray' : ''
              }`}
              onClick={() => handleSelect(t)}>
              {t}
            </div>
          ))}
        </div>
      )}
    </ClickOutsideWrapper>
  );
};
